/**
 * 表セクションの固定行の更新。
 * templateNodes を入力に、固定行の追加・挿入を行う pure function。
 */

import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import { parse } from 'papaparse'
import { SECTION_NAME_MAX_LENGTH } from 'src/constants/section'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getCreateNodeId } from 'src/features/templateEdit/common/createNode'
import { cloneColumnSubtree, remapSubtreeQuestionRefs } from './cellSync'
import {
  getTableColumnNodes,
  getTableFixedRowSectionNodes,
  getTableTemplateRowNode,
} from './selector'
import { buildNewRowSectionNode } from './tableNodeBuilders'
import {
  countTableSectionNodes,
  TABLE_SECTION_MAX_NODE_COUNT,
  type TableSectionUpdate,
} from './tableSection'

type TableFixedRowUpdate = TableSectionUpdate & { activeNodeId: number }

/** 固定行を末尾（テンプレート行の直前）に追加する。 */
export const appendTableFixedRow = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TableFixedRowUpdate =>
  insertTableFixedRowRelative(tableNode, templateNodes, null, 'below')

/**
 * 基準行の上下に固定行を挿入する。
 * anchorRowId が null のときは固定行の末尾（= テンプレート行の直前）へ追加する。
 */
export const insertTableFixedRowRelative = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  anchorRowId: number | null,
  position: 'above' | 'below',
): TableFixedRowUpdate => {
  let nextNodeId = getCreateNodeId(templateNodes)
  const { createdNodes, rowId } = buildNewTableRowNodes(
    tableNode,
    templateNodes,
    () => nextNodeId++,
  )
  const nodesWithRow = { ...templateNodes, ...createdNodes }

  const table = nodesWithRow[tableNode.id] ?? tableNode
  const childIds = [...table.nodes]

  const resolvedAnchor =
    anchorRowId ?? getTableTemplateRowNode(table, nodesWithRow).id
  const resolvedPosition = anchorRowId == null ? 'above' : position

  const anchorIndex = childIds.indexOf(resolvedAnchor)
  if (anchorIndex < 0) {
    throw new Error('基準行が見つかりません')
  }

  const insertIndex =
    resolvedPosition === 'above' ? anchorIndex : anchorIndex + 1
  childIds.splice(insertIndex, 0, rowId)

  return {
    templateNodes: {
      ...nodesWithRow,
      [tableNode.id]: { ...table, nodes: childIds },
    },
    activeNodeId: rowId,
  }
}

export const parseRowHeadings = (text: string): string[] => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\n$/, '')
  // 空セル 1 つだけのコピーは改行のみになる。他の空セルと同じく、既存の見出しを空で上書きする。
  if (normalized === '') {
    return text === '' ? [] : ['']
  }
  const { data, errors } = parse<string[]>(normalized, {
    delimiter: '\t',
    newline: '\n',
  })
  // 引用符が TSV として正しくないと以降の行が 1 セルにまとまるため、引用符を解釈せずに区切る。
  const rows = errors.some(error => error.type === 'Quotes')
    ? normalized.split('\n').map(line => line.split('\t'))
    : data
  // 保存時の検証と同じくコードポイント単位で切り捨てる(サロゲートペアを壊さない)。
  return rows.map(([heading = '']) =>
    [...heading.replace(/\n/g, ' ')].slice(0, SECTION_NAME_MAX_LENGTH).join(''),
  )
}

/** startRowId の固定行から貼り付けられる見出しの最大数。既存の行に加え、表の容量の上限まで行を追加できる。 */
export const getMaxPastableRowHeadingCount = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  startRowId: number,
): number => {
  const rows = getTableFixedRowSectionNodes(tableNode, templateNodes)
  const startIndex = rows.findIndex(row => row.id === startRowId)
  if (startIndex < 0) {
    throw new Error('基準行が見つかりません')
  }
  // 大量の貼り付けで判定前に全行を複製して画面が止まらないよう、行は作らずに見積もる。
  // 新しい行はテンプレート行の列(条件分岐・分岐質問を含む)の複製なので、1 行あたりのノード数は一定。
  const nodesPerRow = countTableSectionNodes(
    getTableTemplateRowNode(tableNode, templateNodes),
    templateNodes,
  )
  const existingRowCount = rows.length - startIndex
  if (nodesPerRow === 0) {
    return Infinity
  }
  // 保存時の検証と同じく、ちょうど上限は許容する。
  const addableRowCount = Math.floor(
    (TABLE_SECTION_MAX_NODE_COUNT -
      countTableSectionNodes(tableNode, templateNodes)) /
      nodesPerRow,
  )
  return existingRowCount + Math.max(0, addableRowCount)
}

/**
 * 貼り付けで書き換わる既存の見出しの数。空の見出しと、貼り付ける値と同じ見出しは数えない。
 * 追加される行はもともと見出しがないので数えない。
 */
export const countOverwrittenRowHeadings = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  startRowId: number,
  headings: string[],
): number => {
  const rows = getTableFixedRowSectionNodes(tableNode, templateNodes)
  const startIndex = rows.findIndex(row => row.id === startRowId)
  if (startIndex < 0) {
    throw new Error('基準行が見つかりません')
  }
  return rows
    .slice(startIndex, startIndex + headings.length)
    .filter((row, i) => {
      const name = row.section?.name ?? ''
      return name.trim() !== '' && name !== headings[i]
    }).length
}

/**
 * startRowId の固定行から下へ、行の見出しを順に設定する。
 * 固定行が足りない分は末尾に追加する。
 */
export const pasteTableRowHeadings = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  startRowId: number,
  headings: string[],
): TableFixedRowUpdate => {
  const rows = getTableFixedRowSectionNodes(tableNode, templateNodes)
  const startIndex = rows.findIndex(row => row.id === startRowId)
  if (startIndex < 0) {
    throw new Error('基準行が見つかりません')
  }

  // 大きなひな形で画面が止まらないよう、足りない行はまとめて作り、採番とノード全体の更新は 1 回にする。
  let nextNodeId = getCreateNodeId(templateNodes)
  const allocateId = () => nextNodeId++
  const addedRowCount = Math.max(0, startIndex + headings.length - rows.length)
  const createdNodes: TemplateNodesDict = {}
  const addedRowIds: number[] = []
  for (let i = 0; i < addedRowCount; i++) {
    const added = buildNewTableRowNodes(tableNode, templateNodes, allocateId)
    Object.assign(createdNodes, added.createdNodes)
    addedRowIds.push(added.rowId)
  }

  // 追加した行は固定行の末尾(= テンプレート行の直前)に並べる。
  const table = templateNodes[tableNode.id] ?? tableNode
  const childIds = [...table.nodes]
  const templateRowIndex = childIds.indexOf(
    getTableTemplateRowNode(table, templateNodes).id,
  )
  if (templateRowIndex < 0) {
    throw new Error('テンプレート行が見つかりません')
  }
  childIds.splice(templateRowIndex, 0, ...addedRowIds)
  const nodes: TemplateNodesDict = {
    ...templateNodes,
    ...createdNodes,
    [tableNode.id]: { ...table, nodes: childIds },
  }

  const targetRowIds = [...rows.map(row => row.id), ...addedRowIds].slice(
    startIndex,
    startIndex + headings.length,
  )
  for (const [i, rowId] of targetRowIds.entries()) {
    const row = nodes[rowId]
    if (!row) {
      throw new Error(`行 ${rowId} が見つかりません`)
    }
    nodes[rowId] = { ...row, section: { ...row.section, name: headings[i] } }
  }

  return { templateNodes: nodes, activeNodeId: startRowId }
}

/**
 * 新しい固定行のノード(行とセル)だけを作る。templateNodes への反映と表の子ノードへの追加は呼び出し側で行う。
 * セルは列の条件分岐・分岐質問まで複製し、追加した時点で列と同じ形にする。
 * id は呼び出し側の allocateId で採番する(getCreateNodeId は全ノード走査なので、複数行を作るときも 1 回だけ呼ぶ)。
 */
const buildNewTableRowNodes = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  allocateId: () => number,
): { createdNodes: TemplateNodesDict; rowId: number } => {
  const rowId = allocateId()
  const createdNodes: TemplateNodesDict = {}
  const cellIds: number[] = []
  /** 列側 uuid → この行の複製先 uuid。 */
  const rowUuidMap = new Map<string, string>()

  for (const column of getTableColumnNodes(tableNode, templateNodes)) {
    // 新しい行のセルはすべて新規採番なので、同一性は引き継がない。
    const { cellNodeId, nodes, uuidByColumnUuid } = cloneColumnSubtree(
      column,
      templateNodes,
      allocateId,
    )
    cellIds.push(cellNodeId)
    Object.assign(createdNodes, nodes)
    uuidByColumnUuid.forEach((rowLocalUuid, columnUuid) => {
      rowUuidMap.set(columnUuid, rowLocalUuid)
    })
  }

  createdNodes[rowId] = {
    ...buildNewRowSectionNode(rowId, 0),
    nodes: cellIds,
  }

  return {
    createdNodes: remapSubtreeQuestionRefs(createdNodes, rowUuidMap),
    rowId,
  }
}
